/**
 * Admin support tickets queue. Plan §Customer Support: response SLAs
 * are 2h for trust_safety, 24h for payment_refund / subscription,
 * 72h for everything else (set at insert time on the ticket row).
 *
 * Replies happen in HelpScout — every ticket mirrors there on submit.
 * The Open in HelpScout link uses the externalThreadId. Status is
 * managed in funkihoo (claim, awaiting, resolve) so the operator
 * doesn't have to remember to sync two systems.
 *
 * Voice rule: matter-of-fact, no security-theater.
 */

import { Badge, type BadgeVariant, Button } from '@funkihoo/ui-web';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type SupportTicketCategoryFilter,
  type SupportTicketRow,
  type SupportTicketStatusFilter,
  useAwaitingSupportTicketMutation,
  useClaimSupportTicketMutation,
  useResolveSupportTicketMutation,
  useSupportTicketsQuery,
} from '../api/support-tickets.js';

const STATUS_TABS: { value: SupportTicketStatusFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'awaiting_user', label: 'Awaiting user' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CATEGORY_OPTIONS: { value: SupportTicketCategoryFilter | ''; label: string }[] = [
  { value: '', label: 'All categories' },
  { value: 'trust_safety', label: 'Trust & safety' },
  { value: 'payment_refund', label: 'Payment / refund' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'profile_help', label: 'Profile help' },
  { value: 'matching_help', label: 'Matching help' },
  { value: 'chat_help', label: 'Chat help' },
  { value: 'liveness_help', label: 'Liveness' },
  { value: 'account_recovery', label: 'Account recovery' },
  { value: 'general', label: 'General' },
];

// HelpScout mailbox URL prefix. Falls back to the conversation URL
// pattern when the env var isn't set (dev). Operators with the
// HelpScout mailbox open in another tab can copy the conversation id
// from the externalThreadId pill if the link doesn't resolve.
const HELPSCOUT_BASE =
  (import.meta.env['VITE_HELPSCOUT_MAILBOX_URL'] as string | undefined) ??
  'https://secure.helpscout.net/conversation';

export function SupportTicketsRoute() {
  const [status, setStatus] = useState<SupportTicketStatusFilter>('open');
  const [category, setCategory] = useState<SupportTicketCategoryFilter | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const query = useSupportTicketsQuery(status, category);

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Support tickets</h1>
        <p className="mt-1 text-neutral-600 text-sm">
          Tickets users submit from /app/home/account → Contact support. Reply happens in HelpScout;
          status (claim → awaiting → resolve) tracks here. SLA: 2h trust &amp; safety, 24h payment /
          subscription, 72h everything else.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => {
              setStatus(t.value);
              setActiveId(null);
            }}
            className={`rounded-md border px-3 py-1.5 font-medium text-sm ${
              status === t.value
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {t.label}
          </button>
        ))}
        <select
          value={category ?? ''}
          onChange={(e) =>
            setCategory((e.target.value || null) as SupportTicketCategoryFilter | null)
          }
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm"
          aria-label="Filter by category"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="ml-auto rounded-md border border-neutral-300 px-3 py-1.5 font-medium text-neutral-700 text-sm hover:bg-neutral-50"
        >
          Refresh
        </button>
        {query.isFetching && <span className="text-neutral-500 text-xs">refreshing…</span>}
      </div>

      {query.isLoading && <p className="text-neutral-500 text-sm">Loading queue…</p>}
      {query.isError && (
        <p className="text-danger-600 text-sm">
          Could not load tickets. Backend down? Check moderation-service.
        </p>
      )}
      {query.data && query.data.tickets.length === 0 && (
        <p className="text-neutral-500 text-sm">No tickets in this view.</p>
      )}

      {query.data && query.data.tickets.length > 0 && (
        <ul className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {query.data.tickets.map((row) => (
            <li key={row.id}>
              <TicketRow
                row={row}
                expanded={activeId === row.id}
                onToggle={() => setActiveId((prev) => (prev === row.id ? null : row.id))}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface TicketRowProps {
  row: SupportTicketRow;
  expanded: boolean;
  onToggle: () => void;
}

function TicketRow({ row, expanded, onToggle }: TicketRowProps) {
  const claim = useClaimSupportTicketMutation();
  const awaiting = useAwaitingSupportTicketMutation();
  const resolve = useResolveSupportTicketMutation();
  const [resolveNote, setResolveNote] = useState('');
  const [finalStatus, setFinalStatus] = useState<'resolved' | 'closed'>('resolved');

  const overdue =
    new Date(row.slaDeadline).getTime() < Date.now() &&
    row.status !== 'resolved' &&
    row.status !== 'closed';
  const helpScoutUrl =
    row.externalThreadId && row.externalThreadId.length > 0
      ? `${HELPSCOUT_BASE}/${encodeURIComponent(row.externalThreadId)}`
      : null;

  return (
    <div className="px-3 py-3 hover:bg-neutral-50">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between text-left"
      >
        <p className="text-neutral-900 text-sm">
          <span className="font-medium">{row.subject}</span>
          <StatusBadge status={row.status} />
          <CategoryBadge category={row.category} />
          {overdue && (
            <Badge variant="danger" density="compact" className="ml-2">
              SLA overdue
            </Badge>
          )}
        </p>
        <span className="text-neutral-400 text-xs">{expanded ? '−' : '+'}</span>
      </button>
      <p className="mt-0.5 text-neutral-500 text-xs">
        {row.requesterEmail}
        {row.userId ? (
          <>
            {' · '}
            <Link
              to={`/users?id=${row.userId}`}
              className="font-mono text-primary-700 hover:underline"
            >
              {row.userId.slice(0, 8)}
            </Link>
          </>
        ) : (
          <span className="text-neutral-400"> · anonymous</span>
        )}
        {' · filed '}
        {new Date(row.createdAt).toLocaleString()}
        {' · SLA '}
        <span className={overdue ? 'font-medium text-danger-700' : ''}>
          {new Date(row.slaDeadline).toLocaleString()}
        </span>
      </p>

      {expanded && (
        <div className="mt-3 space-y-3 rounded-md border border-neutral-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            {helpScoutUrl ? (
              <a
                href={helpScoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-primary-300 bg-white px-3 py-1.5 font-medium text-primary-800 text-sm hover:bg-primary-50"
              >
                Open in HelpScout ↗
              </a>
            ) : (
              <span className="text-neutral-500 text-xs">
                No HelpScout thread yet (mirror still in flight).
              </span>
            )}
            {row.status === 'open' && (
              <Button onClick={() => claim.mutate(row.id)} disabled={claim.isPending}>
                {claim.isPending ? 'Claiming…' : 'Claim'}
              </Button>
            )}
            {(row.status === 'open' || row.status === 'in_progress') && (
              <Button
                variant="outline"
                onClick={() => awaiting.mutate(row.id)}
                disabled={awaiting.isPending}
              >
                {awaiting.isPending ? 'Updating…' : 'Awaiting user'}
              </Button>
            )}
          </div>

          {claim.error && (
            <p className="text-danger-700 text-xs">{(claim.error as Error).message}</p>
          )}
          {awaiting.error && (
            <p className="text-danger-700 text-xs">{(awaiting.error as Error).message}</p>
          )}

          {(row.status === 'open' ||
            row.status === 'in_progress' ||
            row.status === 'awaiting_user') && (
            <div className="space-y-2 rounded-md bg-neutral-50 p-3">
              <p className="font-medium text-neutral-900 text-sm">Resolve</p>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder="Resolution note (required, ends in audit log)"
                rows={3}
                className="w-full rounded-md border border-neutral-300 p-2 text-sm"
                minLength={1}
                maxLength={5000}
              />
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-neutral-600 text-xs">
                  Final status{' '}
                  <select
                    value={finalStatus}
                    onChange={(e) => setFinalStatus(e.target.value as 'resolved' | 'closed')}
                    className="rounded border border-neutral-300 px-2 py-0.5 text-xs"
                  >
                    <option value="resolved">resolved (user acked)</option>
                    <option value="closed">closed (no reply needed)</option>
                  </select>
                </label>
                <Button
                  variant="primary"
                  onClick={() =>
                    resolve.mutate(
                      { id: row.id, resolutionNote: resolveNote.trim(), finalStatus },
                      { onSuccess: () => setResolveNote('') },
                    )
                  }
                  disabled={resolve.isPending || resolveNote.trim().length === 0}
                >
                  {resolve.isPending ? 'Saving…' : 'Save resolution'}
                </Button>
              </div>
              {resolve.error && (
                <p className="text-danger-700 text-xs">{(resolve.error as Error).message}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: BadgeVariant =
    status === 'open'
      ? 'warning'
      : status === 'in_progress'
        ? 'info'
        : status === 'awaiting_user'
          ? 'neutral'
          : status === 'resolved'
            ? 'success'
            : 'neutral';
  return (
    <Badge variant={variant} className="ml-2">
      {status}
    </Badge>
  );
}

function CategoryBadge({ category }: { category: string }) {
  const variant: BadgeVariant = category === 'trust_safety' ? 'danger' : 'neutral';
  return (
    <Badge variant={variant} density="compact" className="ml-2">
      {category}
    </Badge>
  );
}
